import { MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import {
  ROUTER_PATHS,
  TABLE_CONSTANTS_COLUMS,
  emptyCellOneLevel,
  formatCurrencyCell,
  formatDateWithTimeCell,
  hasAllPermissions,
  hasPermission,
} from '@/shared';
import {
  useFetchPlans,
  useRemovePlan,
  useUpdatePlan,
} from '@/shared/actions/erp';
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from '@/shared/components/ui';
import {
  useCheckPermissions,
  useTableFilter,
  useTableServerSideFiltering,
} from '@/shared/hooks';
import { Plan } from '@/shared/interfaces';
import { useUiConfirmModalStore } from '@/store/ui';


export type PlansPageProps = {};

const PlansPage: React.FC<PlansPageProps> = () => {
  useCheckPermissions(PermissionsEnum.api_view_plan);
  const returnUrlPlansPage = ROUTER_PATHS.servicioInternet.planesNav;

  const navigate = useNavigate();

  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* global state
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data
  const {
    data: plansPagingRes,
    isLoading,
    isRefetching,
  } = useFetchPlans({
    page: pageIndex + 1,
    page_size: pageSize,
    nombre: searchTerm,
    ...filterObject,
    filterByState: false,
  });

  ///* mutations
  const deletePlanMutation = useRemovePlan();
  const updSate = useUpdatePlan({
    enableRedirect: false,
  });

  ///* columns
  const columns = useMemo<MRT_ColumnDef<Plan>[]>(
    () => [
      {
        accessorKey: 'nombre',
        header: 'Nombre',
        size: 222,
        Cell: ({ row }) => emptyCellOneLevel(row, 'nombre'),
      },

      // // Velocidad ===================
      {
        accessorKey: 'velocidad_descarga_maxima',
        header: 'Velocidad Descarga Máxima',
        size: 111,
        Cell: ({ row }) => {
          return row.original.velocidad_descarga_maxima
            ? `${row.original.velocidad_descarga_maxima} ${row.original.unidad || ''
            }`
            : 'N/A';
        },
      },
      {
        accessorKey: 'velocidad_descarga_minima',
        header: 'Velocidad Descarga Mínima',
        size: 111,
        Cell: ({ row }) => {
          return row.original.velocidad_descarga_minima
            ? `${row.original.velocidad_descarga_minima} ${row.original.unidad || ''
            }`
            : 'N/A';
        },
      },
      {
        accessorKey: 'velocidad_subida_maxima',
        header: 'Velocidad Subida Máxima',
        size: 111,
        Cell: ({ row }) => {
          return row.original.velocidad_subida_maxima
            ? `${row.original.velocidad_subida_maxima} ${row.original.unidad || ''
            }`
            : 'N/A';
        },
      },
      {
        accessorKey: 'velocidad_subida_minima',
        header: 'Velocidad Subida Mínima',
        size: 111,
        Cell: ({ row }) => {
          return row.original.velocidad_subida_minima
            ? `${row.original.velocidad_subida_minima} ${row.original.unidad || ''
            }`
            : 'N/A';
        },
      },

      {
        accessorKey: 'valor',
        header: 'Valor Plan',
        size: 200,
        Cell: ({ row }) => formatCurrencyCell(row, 'valor'),
      },

      ///* fk ===================
      {
        accessorKey: 'tipo_servicio', // choice
        header: 'Tipo Servicio',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'tipo_servicio'),
      },
      {
        accessorKey: 'tipo_plan', // choice
        header: 'Tipo Plan',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'tipo_plan'),
        // muiTableBodyCellProps: {
        //   align: 'center',
        // },
      },
      {
        accessorKey: 'permanencia', // choice
        header: 'Permanencia',
        size: 111,
        Cell: ({ row }) => emptyCellOneLevel(row, 'permanencia'),
      },
      {
        accessorKey: 'unidad', // choice
        header: 'Unidad Medida velocidad',
        size: 111,
        Cell: ({ row }) => emptyCellOneLevel(row, 'unidad'),
      },

      {
        accessorKey: 'codigo',
        header: 'Código',
        size: 222,
        Cell: ({ row }) => emptyCellOneLevel(row, 'codigo'),
      },
      // state ---------
      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          return (
            <SwitchComponente
              estadoSwitch={row.original.estado}
              titulo={row.original.estado ? 'Activo' : 'Inactivo'}
              setEstadoSwitch={state => {
                if (!hasPermission(PermissionsEnum.api_change_plan)) return;

                setConfirmDialog({
                  isOpen: true,
                  title: 'Cambiar Estado',
                  subtitle:
                    '¿Está seguro que desea cambiar el estado de este registro ?',
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    updSate.mutate({
                      id: row.original.id_plan!,
                      data: { estado: !!state } as any,
                    });
                  },
                });
              }}
            />
          );
        },
      },
      {
        accessorKey: 'fecha_creacion',
        header: 'Fecha de Creación',
        size: 200,
        enableColumnFilter: true,
        filterVariant: 'date',
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_creacion'),
      },
      {
        accessorKey: 'fecha_modificacion',
        header: 'Fecha de Modificación',
        size: 200,
        enableColumnFilter: true,
        filterVariant: 'date',
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_modificacion'),
      },
    ],
    []
  );

  ///* handlers
  const onEdit = (original: Plan) => {
    navigate(`${returnUrlPlansPage}/editar/${original.id_plan}`);
  };

  const onDelete = (original: Plan) => {
    setConfirmDialog({
      isOpen: true,
      subtitle: '¿Está seguro que desea eliminar el Plan seleccionado?',
      title: 'Confirmar eliminación',
      onConfirm: () => {
        deletePlanMutation.mutate(original.id_plan!);
      },
      onClose() {
        setConfirmDialogIsOpen(false);
      },
    });
  };

  return (
    <SingleTableBoxScene
      title="Planes"
      urlRedirect={`${returnUrlPlansPage}/crear`}
      enableRedirect={hasPermission(PermissionsEnum.api_add_plan)}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="por nombre"
      />

      <GeneralTable<Plan>
        columns={columns}
        data={plansPagingRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        // // filters - llega al back
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={plansPagingRes?.data?.count}
        // // actions ========
        enableColumnActions={hasAllPermissions([
          PermissionsEnum.api_change_ciudad,
        ])}
        actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
        // crud
        canEdit={hasAllPermissions([PermissionsEnum.api_change_plan])}
        onEdit={onEdit}
        canDelete={false}
        onDelete={onDelete}
      />
    </SingleTableBoxScene>
  );
};

export default PlansPage;
